/**
 * RoleGuard — proactively renders access-denied when the authenticated
 * user doesn't have the required role.
 *
 * V0.5.0 backend handoff Q4 (locked decision): the role check happens
 * client-side BEFORE the app shell renders. The user shouldn't see a
 * half-loaded dashboard before being told they can't be here. The
 * backend still enforces (every request re-checks); this is only a
 * rendering decision.
 *
 * Wrapped around the router-level layout so a non-AM user who somehow
 * authenticates gets a single full-page access-denied with a sign-out
 * button instead of any app chrome.
 */
import type { ReactNode } from "react";
import { useAuth, useIsAccountManager } from "./AuthContext";
import { signOut } from "./tokenProvider";

export interface RoleGuardProps {
  children: ReactNode;
}

export function RoleGuard({ children }: RoleGuardProps) {
  const isAM = useIsAccountManager();
  const { email, displayName, roles } = useAuth();

  if (isAM) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-page p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-white p-6 shadow-card">
        <div className="text-xs font-semibold uppercase tracking-wider text-red-600">
          Access denied
        </div>
        <h1 className="mt-1 text-xl font-semibold text-gray-800">
          You don&apos;t have access to the MIPS Dashboard.
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          {displayName ?? email ?? "Your account"} authenticated, but
          isn&apos;t assigned the <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">RCM_ACCOUNT_MANAGER</code> role.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          If you should have access, contact your administrator and ask
          to be added to the <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">RCM_ACCOUNT_MANAGER</code> Cognito group.
        </p>
        {roles.length > 0 ? (
          <div className="mt-4 rounded border border-border bg-gray-50 p-3">
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Your current roles
            </div>
            <div className="mt-1 text-xs text-gray-700">
              {roles.join(", ")}
            </div>
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => void signOut({ reload: true })}
          className="mt-5 w-full rounded-button bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
